import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { DATA_LAYERS } from '../../../lib/dataLayers';
import { useAgbTimeSeries } from '../../../hooks/useAgbTimeSeries';
import { AgbTimeSeriesPoint } from '../../../lib/types';
import {
  CHART_TOOLTIP_STYLE,
  CHART_AXIS_STYLE,
  CHART_GRID_STROKE,
  CHART_AXIS_STROKE,
  CHART_PRIMARY_COLOR,
} from '../../../lib/chartStyles';
import { LayerHeader } from './LayerHeader';
import { StatCard } from './StatCard';
import { AboutLayer } from './AboutLayer';
import { GlossaryTerm } from '../../shared/GlossaryTerm';
import {
  LayerLoadingLabel,
  LayerErrorAlert,
  LayerLiveBadge,
  SkeletonPulseBlock,
  SkeletonStatCardGrid,
} from './contentPrimitives';

interface AgbContentProps {
  millCode: string | null;
  driveTimeMinutes: number;
  // Year the map overlay is currently rendering. Echoed in the chart card
  // subheading so users scrubbing the slider see the chart and map stay
  // visually linked.
  agbYear: number;
}

interface AgbStats {
  latest: AgbTimeSeriesPoint;
  first: AgbTimeSeriesPoint;
  mean: number;
  min: number;
  max: number;
  delta: number;
}

const computeStats = (points: AgbTimeSeriesPoint[]): AgbStats | null => {
  if (points.length === 0) return null;
  const sorted = [...points].sort((a, b) => a.year - b.year);
  const values = sorted.map(p => p.avgDensity);
  const first = sorted[0];
  const latest = sorted[sorted.length - 1];
  return {
    latest,
    first,
    mean: values.reduce((s, v) => s + v, 0) / values.length,
    min: Math.min(...values),
    max: Math.max(...values),
    delta: latest.avgDensity - first.avgDensity,
  };
};

const AgbSkeleton: React.FC = () => (
  <div className="space-y-4">
    <LayerLoadingLabel layer={DATA_LAYERS.aboveground_biomass} />
    <SkeletonPulseBlock className="h-[260px]" />
    <SkeletonStatCardGrid />
  </div>
);

export const AgbContent: React.FC<AgbContentProps> = ({ millCode, driveTimeMinutes, agbYear }) => {
  const layer = DATA_LAYERS.aboveground_biomass;
  const { data, isLoading, error } = useAgbTimeSeries(millCode, driveTimeMinutes);

  return (
    <div className="space-y-6">
      <LayerHeader layer={layer} />

      <AboutLayer>
        <p>
          <GlossaryTerm term="agb">Above-ground biomass (AGB)</GlossaryTerm> is the total
          dry mass of live trees within the supply shed &mdash; trunks, branches, and
          leaves. It is the most directly measurable component of the forest carbon
          pool and the one most relevant to wood supply, fire risk, and forest carbon
          credits.
        </p>
        <p>
          Values are reported as biomass density (<GlossaryTerm term="mg_ha">Mg/ha</GlossaryTerm>),
          not carbon. About 47% of biomass is carbon by mass (IPCC default),
          so <span className="font-roboto-mono">1 Mg AGB &asymp; 0.47 Mg C</span>.
        </p>
        <p>
          A rising trend usually reflects forest growth or recovery from past disturbance.
          A falling trend points to disturbance: harvest, fire, insect outbreaks, or
          land-use change. Year-to-year noise is normal &mdash; focus on the multi-year
          direction rather than single-year spikes.
        </p>
      </AboutLayer>

      {isLoading && <AgbSkeleton />}

      {!isLoading && error && (
        <LayerErrorAlert layer={layer} details={error} />
      )}

      {!isLoading && data && data.points.length > 0 && (() => {
        const points = data.points.map(p => ({
          year: p.year,
          avgDensity: p.avgDensity,
        }));
        const stats = computeStats(data.points);
        const fmt = (n: number) => `${n.toFixed(2)} Mg/ha`;
        const deltaSign = stats && stats.delta >= 0 ? '+' : '';
        const deltaColor = stats && stats.delta >= 0 ? 'text-green-400' : 'text-red-400';

        return (
          <>
            <LayerLiveBadge />

            <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
              <div className="flex items-baseline justify-between mb-4">
                <h4 className="text-base font-roboto font-bold text-figma-text">
                  AGB Density Trend <span className="text-neutral-500 font-normal">(Mg/ha)</span>
                </h4>
                <span className="text-[11px] font-roboto-mono uppercase tracking-wider text-figma-lime/80">
                  Map: {agbYear}
                </span>
              </div>
              <ResponsiveContainer width="100%" height={260} minWidth={0}>
                <LineChart data={points} margin={{ top: 5, right: 12, left: 8, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                  <XAxis
                    dataKey="year"
                    stroke={CHART_AXIS_STROKE}
                    style={CHART_AXIS_STYLE}
                    tickMargin={6}
                  />
                  <YAxis
                    stroke={CHART_AXIS_STROKE}
                    style={CHART_AXIS_STYLE}
                    domain={['dataMin - 1', 'dataMax + 1']}
                    tickFormatter={(v: number) => v.toFixed(1)}
                    width={44}
                  />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP_STYLE}
                    formatter={(value: number | undefined) => [
                      `${(value ?? 0).toFixed(2)} Mg/ha`,
                      'AGB density',
                    ]}
                    labelFormatter={(label) => `Year ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="avgDensity"
                    stroke={CHART_PRIMARY_COLOR}
                    strokeWidth={2}
                    dot={{ r: 2.5 }}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
              <p className="text-xs font-roboto italic text-neutral-500 mt-2 leading-relaxed">
                Each point is the area-weighted mean AGB density across the supply shed
                for that year. Patterns over 5+ years are more meaningful than single-year
                spikes &mdash; annual variability can reflect satellite acquisition timing,
                weather, or noise.
              </p>
            </div>

            {stats && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <StatCard label={`Latest (${stats.latest.year})`} value={fmt(stats.latest.avgDensity)} />
                  <StatCard label={`Mean (${stats.first.year}-${stats.latest.year})`} value={fmt(stats.mean)} />
                  <StatCard label="Minimum" value={fmt(stats.min)} />
                  <StatCard label="Maximum" value={fmt(stats.max)} />
                </div>
                <div className={`bg-figma-card rounded-figma-panel p-5 border shadow-figma-card ${
                  stats.delta >= 0 ? 'border-green-400/40' : 'border-red-400/40'
                }`}>
                  <div className="flex items-center gap-4">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                      stats.delta >= 0 ? 'bg-green-500/15' : 'bg-red-500/15'
                    }`}>
                      <svg className={`w-6 h-6 ${deltaColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        {stats.delta >= 0 ? (
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-body-sm font-roboto text-neutral-400 mb-0.5">
                        {stats.first.year}&mdash;{stats.latest.year} change
                      </p>
                      <p className={`text-2xl font-roboto-mono font-bold ${deltaColor}`}>
                        {deltaSign}{stats.delta.toFixed(2)} Mg/ha
                      </p>
                      <p className="text-xs font-roboto text-neutral-500 mt-1">
                        {stats.delta >= 0 ? 'Net biomass gain' : 'Net biomass loss'} over the {stats.latest.year - stats.first.year}-year window
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        );
      })()}
    </div>
  );
};
